import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import { FoodItems } from "../modal-components/FoodItems";
import {
  transformFoodsForMeals,
  transformFoodsToGetTheMeal,
} from "../../../../../utils/nutrition-util";
import { FoodActionsForBuildMeal } from "../modal-components/FoodActionsForBuildMeal";
import { EmptyFood } from "../empty-states/EmptyFood";
import { simpleHash } from "../../../../../utils/string-helpers";

export const BuildMealModal = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const { query, mostUsedMealPlans, mealsByCoach } = locals;
  const allMeals = mealsByCoach || mostUsedMealPlans["meals"];
  const { mealId } = query;
  const title = mealId ? t("editMealPlan.buildMealModal.titleEdit") : t("editMealPlan.buildMealModal.titleCreate");
  const currentMeal = allMeals.find(({ _id }) => _id === mealId) || {
    name: "",
    description: "",
    foods: [],
  };
  const {
    name: currentMealName,
    description: currentMealDescription,
    foods: currentMealFoods,
  } = currentMeal;

  const initialState = {
    name: currentMealName || "",
    description: currentMealDescription || "",
    foods: transformFoodsToGetTheMeal(currentMealFoods) || [],
  };
  const [state, setState] = useState(initialState);

  const { name, description, foods } = state;

  /**
   * Update the quantity number of the foods
   */
  const handleUpdateItems = useCallback(
    (index, updatedItem) => {
      updateState({
        foods: foods.map((food, i) =>
          i === index ? { ...food, ...updatedItem } : food
        ),
      });
    },
    [foods]
  );

  /**
   * Update the state
   * @param {*} newState
   * @returns
   */
  const updateState = (newState) =>
    setState((prevState) => ({ ...prevState, ...newState }));

  /**
   * Create / edit a meal
   */
  const handleSend = () => {
    const payload = {
      name,
      description,
      foods: transformFoodsForMeals(foods),
    };
    const requestId = simpleHash(JSON.stringify(payload));

    const body = {
      ...payload,
      requestId,
    };

    const action = mealId ? `handle-meal?mealId=${mealId}` : `handle-meal`;
    submit(`/actions/meal-planner/${action}`, {
      method: "POST",
      body,
    });
  };

  /**
   * Get the input values
   * @param {*} e
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateState({ [name]: value });
  };

  /**
   * Function to add a food
   * @param {*} item
   */
  const handleAddItem = useCallback(
    (items) => {
      updateState({ foods: [...foods, ...items] });
    },
    [foods]
  );

  /**
   * Function to remove a food
   * @param {*} index
   */
  const handleRemoveItem = useCallback(
    (index) => {
      const updatedItems = [...foods];
      updatedItems.splice(index, 1);
      updateState({ foods: updatedItems });
    },
    [foods]
  );

  return (
    <Modal
      className="create-new-meal-modal"
      title={title}
      backButton={true}
      toClear={["mealId"]}
    >
      <div className="body body-foods">
        <div>
          <div className="inputs">
            <div>
              <label className="label" htmlFor="name">
                {t("editMealPlan.buildMealModal.mealName")}
              </label>
              <input
                maxLength={100}
                type="text"
                placeholder={t("editMealPlan.buildMealModal.mealNamePlaceholder")}
                name="name"
                value={name}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <label className="label" htmlFor="description">
                {t("editMealPlan.buildMealModal.mealDescription")}
              </label>
              <input
                maxLength={100}
                type="text"
                name="description"
                placeholder={t("editMealPlan.buildMealModal.descriptionPlaceholder")}
                value={description}
                onChange={handleInputChange}
              ></input>
            </div>
          </div>
          <div className="add-food-main-container">
            <FoodActionsForBuildMeal
              locals={locals}
              searchFilter={"all"}
              api={api}
              onAddItem={handleAddItem}
            />
            <div className="added-text">
              <span className="clipped font-semibold h-5">{t("editMealPlan.buildMealModal.added")}</span>
            </div>

            <div className="food">
              {foods.length > 0 ? (
                <FoodItems
                  items={foods}
                  onRemoveItem={handleRemoveItem}
                  onUpdateItems={handleUpdateItems}
                />
              ) : (
                <EmptyFood label="all" variant="meal" />
              )}
            </div>
          </div>
        </div>
        <div className={"buttons justify-end"}>
          <button
            data-button-type="primary"
            onClick={handleSend}
            disabled={
              !name.trim() ||
              foods.length === 0 ||
              foods.some(
                ({ customQuantityNumber }) =>
                  customQuantityNumber === "" || isNaN(customQuantityNumber)
              )
            }
          >
            {t("editMealPlan.buildMealModal.saveMeal")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

BuildMealModal.propTypes = {
  locals: PropTypes.object,
  api: PropTypes.any,
};
